'use client';
import Link from 'next/link';
import { GameType, dateToSimpleDate } from '@puzzlio/core';
import { CalendarIcon, WeekInProgressIcon, WeekSolvedIcon } from '@puzzlio/ui';
import { cn } from '@/app/lib/utils';
import { GAME_CONFIG } from '@/app/games/config';
import { DAY_LABELS, DayState, useWeekDays } from '@/app/components/new-ui/molecules/week-bar';

const TILE =
  'relative flex size-8 shrink-0 items-center justify-center rounded-md font-quicksand text-xs font-bold text-gray-500';

function DayTile({ label, state }: { label: string; state: DayState }) {
  if (state === 'complete') {
    return (
      <span className={TILE} aria-label={`${label}, solved`}>
        <WeekSolvedIcon className="size-[33px]" />
      </span>
    );
  }
  return (
    // A future day is the plain tile, label and all, at 40%.
    <span className={cn(TILE, state !== 'incomplete' && 'bg-white', state === 'unavailable' && 'opacity-40')}>
      {state === 'incomplete' && <WeekInProgressIcon className="absolute inset-0 size-full" />}
      <span
        className={cn(
          'relative',
          // The label is haloed in white so it reads across the tile's grey quarters and their seams.
          state === 'incomplete' && '[-webkit-text-stroke:4px_var(--white)] [paint-order:stroke_fill]'
        )}
      >
        {label}
      </span>
    </span>
  );
}

/** A game card's week, along its foot: each day opens that day's puzzle, and the calendar the archive. */
export function HomeWeekStrip({
  gameType,
  disabled,
  className,
}: {
  gameType: GameType;
  disabled: boolean;
  className?: string;
}) {
  const { gameURL, archiveURL, label } = GAME_CONFIG[gameType];
  const week = useWeekDays(gameType, dateToSimpleDate(new Date()), disabled);

  return (
    // Fixed height, so the band doesn't jump when the days arrive after mount. The card passes its fill.
    <div
      className={cn(
        'flex h-[52px] items-center justify-between border-b-2 border-white px-4 md:justify-center md:gap-2.5 md:border-b-0',
        className
      )}
    >
      {week && (
        <>
          {week.map(({ date, state }, idx) =>
            state === 'unavailable' ? (
              <DayTile key={date} label={DAY_LABELS[idx]} state={state} />
            ) : (
              <Link key={date} prefetch={false} href={gameURL(date)} className="transition-transform hover:scale-110">
                <DayTile label={DAY_LABELS[idx]} state={state} />
              </Link>
            )
          )}
          <Link
            prefetch={false}
            href={archiveURL()}
            aria-label={`${label} archive`}
            className={cn(TILE, 'bg-white transition-transform hover:scale-110')}
          >
            <CalendarIcon className="size-5" />
          </Link>
        </>
      )}
    </div>
  );
}
